import React from 'react';
import styled from '@emotion/styled';
import get from 'lodash/get';

import { Link } from './Link';
import { Image } from '../image/Image';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  isActive?: boolean,
  isVertical?: boolean,
  mode: 'default' | 'vertical',
  title?: string,
  image?: string,
  uuid?: string,
};

export const MenuLink = ({ isActive, isVertical, mode, title, image, uuid, ...rest }: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Navbar, mode);

  return (
    <MenuNavLink isActive={isActive} isVertical={isVertical} componentTheme={componentTheme} {...rest}>
      <StyledLink isActive={isActive} componentTheme={componentTheme} isVertical={isVertical}>
        {isVertical && componentTheme.Image.visible && (
          <ImageWrapper
            mode="square"
            scope="kiosk_navigation_image_view"
            fallbackScope="grid"
            uuid={uuid}
            externalSrc={image}
            sizes="33vw"
            mb={componentTheme.Title.visible && title ? 4 : 0}
            mx="auto"
            width={parseInt(componentTheme.Image.width, 10)}
            componentTheme={componentTheme}
          />
        )}
        {componentTheme.Title.visible ? title : null}
      </StyledLink>
    </MenuNavLink>
  );
};

const MenuNavLink = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: ${({ isVertical }) => isVertical && 'column'};
  width: 100%;
  height: 100%;
  align-items: center;
  text-align: center;
  color: ${({ theme, componentTheme, isActive }) =>
    isActive ? get(componentTheme, 'Link.activeColor', theme.color.brand) : componentTheme.Link.color};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ componentTheme }) => componentTheme.Link.fontWeight};
  font-family: ${({ theme, componentTheme }) =>
    componentTheme.Link.secondaryFontFamily ? theme.typography.secondaryFontFamily : 'inherit'};
  z-index: 0;
  transition: ${({ theme }) => theme.motion.transition};
  text-decoration: ${({ componentTheme }) => componentTheme.Link.textDecoration};
  text-transform: ${({ componentTheme }) => componentTheme.Link.textTransform};
  overflow-wrap: break-word;

  box-shadow: ${({ theme, componentTheme, isActive, isVertical }) =>
    isActive &&
    !isVertical &&
    (componentTheme.Link.boxShadow ||
      `inset 0px -4px 0px ${get(componentTheme, 'Link.activeColor', theme.color.brand)}`)};
  white-space: ${({ isVertical }) => isVertical && 'initial'};

  &:hover {
    color: ${({ theme, componentTheme, isVertical }) => !isVertical && (componentTheme.Link.activeColor || theme.color.brand)};
  }

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ componentTheme }) => componentTheme.Link.fontSize};
  }

  @media (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    font-size: ${({ componentTheme }) => componentTheme.Link.fontSize};
    padding: ${({ isVertical }) => isVertical && '32px 16px'};
  }
`;

const StyledLink = styled('span')`
  width: 100%;
  padding: ${({ componentTheme }) => componentTheme.Link.padding};
  background: ${({ componentTheme, isActive }) =>
    isActive
      ? get(componentTheme, 'Link.activeBackgroundColor', componentTheme.Link.backgroundColor)
      : componentTheme.Link.backgroundColor};
  border-radius: ${({ componentTheme }) => componentTheme.Link.borderRadius};
  text-align: center;

  ${({ componentTheme }) => componentTheme.Link.truncateLines && `
    display: -webkit-box;
    -webkit-line-clamp: ${componentTheme.Link.truncateLines};
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: normal;
  `};

  &:active {
    background-color: ${({ componentTheme }) => get(componentTheme, 'Link.activeBackgroundColor', componentTheme.Link.backgroundColor)};
  }
`;

const ImageWrapper = styled(Image, { shouldForwardProp: prop => prop !== 'componentTheme' })`
  border-radius: ${({ componentTheme }) => componentTheme.Image.borderRadius};
`;

MenuLink.defaultProps = {
  mode: 'default',
};
